<template>
  <div class="fee-receipt-report-container">
    <el-card class="search-wrapper">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('feeReceiptReport.title') }}</span>
        <div>
          <el-radio-group v-model="reportType" size="small" @change="_onReportTypeChange">
            <el-radio-button label="issued">{{ $t('feeReceiptReport.tabIssued') }}</el-radio-button>
            <el-radio-button label="voided">{{ $t('feeReceiptReport.tabVoided') }}</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-date-picker v-model="conditions.qstartTime" type="date"
            :placeholder="$t('feeReceiptReport.startTime')" style="width:100%" value-format="yyyy-MM-dd" />
        </el-col>
        <el-col :span="6">
          <el-date-picker v-model="conditions.qendTime" type="date"
            :placeholder="$t('feeReceiptReport.endTime')" style="width:100%" value-format="yyyy-MM-dd" />
        </el-col>
        <el-col :span="4">
          <el-button type="primary" size="small" @click="_queryReport" icon="el-icon-search">
            {{ $t('common.search') }}
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 汇总统计 -->
    <el-card class="summary-wrapper" v-if="loaded">
      <div class="summary-cards">
        <div class="summary-card">
          <div class="card-label">{{ $t('feeReceiptReport.totalCount') }}</div>
          <div class="card-value">{{ summary.totalCount }}</div>
        </div>
        <div class="summary-card">
          <div class="card-label">{{ $t('feeReceiptReport.totalAmount') }}</div>
          <div class="card-value amount">{{ summary.totalAmount.toFixed(2) }}</div>
        </div>
      </div>
    </el-card>

    <!-- 数据表格 -->
    <el-card class="list-wrapper">
      <div slot="header" class="flex justify-between">
        <span>
          {{ reportType === 'issued' ? $t('feeReceiptReport.issuedReport') : $t('feeReceiptReport.voidedReport') }}
        </span>
        <el-button size="small" type="primary" @click="_exportExcel" icon="el-icon-download">
          {{ $t('common.export') }}
        </el-button>
      </div>
      <el-table :data="reportData" border style="width: 100%" v-loading="loading">
        <el-table-column prop="receiptCode" :label="$t('feeReceiptReport.receiptCode')" align="center" width="120" />
        <el-table-column prop="objType" :label="$t('feeReceiptReport.objType')" align="center" width="80">
          <template slot-scope="scope">
            {{ scope.row.objType === '3333' ? $t('feeReceiptReport.houseFee') : $t('feeReceiptReport.parkingFee') }}
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('feeReceiptReport.feeName')" align="center" />
        <el-table-column prop="objName" :label="$t('feeReceiptReport.roomName')" align="center" />
        <el-table-column prop="payObjName" :label="$t('feeReceiptReport.payObjName')" align="center" />
        <el-table-column prop="amount" :label="$t('feeReceiptReport.amount')" align="center" />
        <el-table-column prop="receivableAmount" :label="$t('feeReceiptReport.receivableAmount')" align="center" />
        <el-table-column prop="createTime" :label="$t('feeReceiptReport.payTime')" align="center" width="160" />
      </el-table>
      <el-pagination :current-page.sync="page.current" :page-sizes="[10, 20, 30, 50]" :page-size="page.size"
        :total="page.total" layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </el-card>
  </div>
</template>

<script>
import { queryFeeReceipt, exportFeeReceipt } from '@/api/fee/feeReceiptApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'FeeReceiptReport',
  data() {
    return {
      loading: false,
      loaded: false,
      reportType: 'issued',
      communityId: getCommunityId(),
      conditions: {
        qstartTime: '',
        qendTime: ''
      },
      reportData: [],
      summary: {
        totalCount: 0,
        totalAmount: 0
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this._queryReport()
  },
  methods: {
    async _queryReport() {
      this.loading = true
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          communityId: this.communityId,
          qstartTime: this.conditions.qstartTime || '',
          qendTime: this.conditions.qendTime || '',
          // 后端按状态过滤：已开收据='0'（正常），作废单据='2002'
          statusCd: this.reportType === 'voided' ? '2002' : '0'
        }
        const res = await queryFeeReceipt(params)
        let data = res.data || []
        const totalRecords = res.total || 0

        this.reportData = data
        this.page.total = totalRecords

        // 汇总（后端已按状态过滤，直接统计当前页数据展示）
        this.summary.totalCount = totalRecords
        let totalAmount = 0
        data.forEach(item => { totalAmount += parseFloat(item.amount || 0) })
        this.summary.totalAmount = totalAmount
      } catch (e) {
        console.error('查询报表失败', e)
        this.$message.error(this.$t('feeReceiptReport.fetchError'))
      } finally {
        this.loading = false
        this.loaded = true
      }
    },
    _onReportTypeChange() {
      this.page.current = 1
      this._queryReport()
    },
    async _exportExcel() {
      if (!this.reportData || this.reportData.length === 0) {
        this.$message.warning(this.$t('feeReceiptReport.noDataExport'))
        return
      }
      try {
        const params = {
          qstartTime: this.conditions.qstartTime || '',
          qendTime: this.conditions.qendTime || '',
          statusCd: this.reportType === 'voided' ? '2002' : '0'
        }
        await exportFeeReceipt(params)
        this.$message.success(this.$t('feeReceiptReport.exportSuccess'))
        this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error(error.message || this.$t('feeReceiptReport.exportError'))
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this._queryReport()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._queryReport()
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-receipt-report-container {
  padding: 20px;

  .search-wrapper { margin-bottom: 20px; }
  .list-wrapper { margin-top: 20px; }

  .summary-wrapper {
    .summary-cards {
      display: flex;
      gap: 20px;
      .summary-card {
        flex: 1;
        text-align: center;
        padding: 20px;
        background: #f5f7fa;
        border-radius: 8px;
        .card-label { font-size: 13px; color: #909399; margin-bottom: 8px; }
        .card-value { font-size: 28px; font-weight: bold; color: #303133; }
        .card-value.amount { color: #F56C6C; }
      }
    }
  }
}
</style>
